import { request } from './core.api';

export type PropertyStatus = 'Rented' | 'Vacant' | 'UnderRenovation' | 'ForSale';
export type PropertyStructureType = 'Standalone' | 'Building' | 'Unit';
export type PropertyNotificationPreferenceKind = 'contractEnd' | 'epcExpiry' | 'insuranceExpiry';
export type PropertyNotificationPreferenceUnit = 'months' | 'days';

export interface PropertyNotificationPreferenceDto {
  kind: PropertyNotificationPreferenceKind;
  enabled: boolean;
  leadTimeValue: number;
  leadTimeUnit: PropertyNotificationPreferenceUnit;
  leadTimeDays: number;
}

export interface PropertyNotificationPreferenceInput {
  kind: PropertyNotificationPreferenceKind;
  enabled: boolean;
  leadTimeValue: number;
}

export interface PropertyUnitSummaryDto {
  id: string;
  displayName: string;
  type: string;
  status: PropertyStatus;
  tenantCount: number;
  documentCount: number;
  activeRentAmount?: number;
  openReminderCount: number;
  overdueReminderCount: number;
}

export interface PropertyGalleryPhotoDto {
  id: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  downloadUrl: string;
  createdAt: string;
}

export interface PropertyDto {
  id: string;
  address: string;
  displayName?: string;
  name?: string;
  type: string;
  structureType?: PropertyStructureType;
  parentPropertyId?: string;
  unitLabel?: string;
  status: PropertyStatus;
  description?: string;
  notes?: string;
  notificationPreferences: PropertyNotificationPreferenceDto[];
  galleryPhotos?: PropertyGalleryPhotoDto[];
  units?: PropertyUnitSummaryDto[];
  occupiedUnitCount?: number;
  vacantUnitCount?: number;
  tenantCount: number;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyListItemDto extends PropertyDto {
  activeRentAmount?: number;
  openReminderCount: number;
  overdueReminderCount: number;
}

export interface CreatePropertyUnitRequest {
  label: string;
  type: string;
  status: PropertyStatus;
  description?: string;
  notes?: string;
}

export interface CreatePropertyRequest {
  address: string;
  name?: string;
  type: string;
  structureType: PropertyStructureType;
  status: PropertyStatus;
  unitLabel?: string;
  description?: string;
  notes?: string;
  notificationPreferences?: PropertyNotificationPreferenceInput[];
  units?: CreatePropertyUnitRequest[];
}

export interface UpdatePropertyRequest extends CreatePropertyRequest {}

export interface CreatePropertyGalleryPhotoRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface PropertyGalleryPhotoUploadUrlDto {
  photoId: string;
  uploadUrl: string;
}

export interface ImportPropertiesCsvRequest {
  content: string;
}

export interface PropertyCsvImportResultDto {
  importedRowCount: number;
  createdPropertyCount: number;
  createdTenantCount: number;
  firstPropertyId?: string;
  firstTenantId?: string;
}

export const propertiesApi = {
  getAll: () => request<PropertyListItemDto[]>('/properties'),
  getById: (id: string) => request<PropertyDto>(`/properties/${id}`),
  create: (data: CreatePropertyRequest) => request<PropertyDto>('/properties', { method: 'POST', body: JSON.stringify(data) }),
  importCsv: (data: ImportPropertiesCsvRequest) =>
    request<PropertyCsvImportResultDto>('/properties/import-csv', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdatePropertyRequest) => request<PropertyDto>(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  createGalleryPhotoUpload: (id: string, data: CreatePropertyGalleryPhotoRequest) =>
    request<PropertyGalleryPhotoUploadUrlDto>(`/properties/${id}/gallery/photos`, { method: 'POST', body: JSON.stringify(data) }),
  getGalleryPhotoDownloadUrl: (photoId: string) => request<{ url: string }>(`/property-gallery-photos/${photoId}/download`),
  deleteGalleryPhoto: (photoId: string) => request<void>(`/property-gallery-photos/${photoId}`, { method: 'DELETE' }),
  delete: (id: string) => request<void>(`/properties/${id}`, { method: 'DELETE' }),
};

export const DEFAULT_PROPERTY_NOTIFICATION_PREFERENCES: PropertyNotificationPreferenceDto[] = [
  { kind: 'contractEnd', enabled: true, leadTimeValue: 3, leadTimeUnit: 'months', leadTimeDays: 90 },
  { kind: 'epcExpiry', enabled: true, leadTimeValue: 6, leadTimeUnit: 'months', leadTimeDays: 180 },
  { kind: 'insuranceExpiry', enabled: true, leadTimeValue: 30, leadTimeUnit: 'days', leadTimeDays: 30 },
];

export function getPropertyNotificationPreferences(property?: Pick<PropertyDto, 'notificationPreferences'> | null): PropertyNotificationPreferenceDto[] {
  const lookup = new Map((property?.notificationPreferences || []).map((preference) => [preference.kind, preference]));
  return DEFAULT_PROPERTY_NOTIFICATION_PREFERENCES.map((preference) => lookup.get(preference.kind) || preference);
}
